import { T, TEAM_COLORS } from "../tokens";

// Decision point helpers
const createDecisionPoint = (frame, timestamp, players) => ({
  frame, timestamp,
  baseState: players,
  id: Date.now(),
});

const runSimulation = (instructions, baseState) => {
  // MVP: return modified positions based on instructions
  return baseState.map(p => {
    const instr = instructions.find(i => i.player_id === p.tracker_id);
    if (!instr) return p;
    return { ...p, scenario_action: instr.action };
  });
};

export default function ScenarioBuilder({
  scenario, frameRows, onUpdateInstruction, onSetBallTarget, outcome,
}) {
  if (!scenario) return null;
  const players = (frameRows || []).filter(r=>r.role!=="ball")
    .sort((a,b)=>a.team_id-b.team_id || a.tracker_id-b.tracker_id);

  return (
    <div style={{background:T.panel,borderTop:`1px solid ${T.border}`,
      maxHeight:220,overflowY:"auto",padding:"10px 14px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:11,fontWeight:800,color:scenario.color,display:"flex",alignItems:"center",gap:6}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:scenario.color}}/>
          {scenario.label} — branched at frame {scenario.frame}
        </div>
        {outcome && (
          <div style={{display:"flex",gap:14,fontSize:10}}>
            <Stat label="Pass success" value={`${outcome.passSuccess}%`}/>
            <Stat label="Shot prob" value={`${outcome.shotProb}%`}/>
            <Stat label="xT" value={outcome.xT}/>
            <Stat label="Space" value={`${outcome.spaceCreated} m²`}/>
            <Stat label="Goal prob" value={`${outcome.goalProb}%`} highlight/>
          </div>
        )}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        {/* Player instructions */}
        <div>
          <div style={{fontSize:9,color:T.textDim,marginBottom:5}}>PLAYER INSTRUCTIONS</div>
          {players.length === 0 && <div style={{fontSize:11,color:T.textDim}}>No players at this frame.</div>}
          {players.map(p=>(
            <div key={p.tracker_id} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:TEAM_COLORS[p.team_id],flexShrink:0}}/>
              <span style={{fontSize:10,color:T.textSec,width:26,flexShrink:0}}>#{p.tracker_id}</span>
              <input
                value={scenario.instructions?.[p.tracker_id] || ""}
                onChange={e=>onUpdateInstruction(p.tracker_id, e.target.value)}
                placeholder="e.g. Pass wide, Diagonal run…"
                style={{
                  flex:1,background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,
                  color:T.textPri,fontSize:10.5,padding:"3px 7px",fontFamily:"inherit",
                }}
              />
            </div>
          ))}
        </div>

        {/* Ball target — simplified MVP editor (Stage 9 Step 3) */}
        <div>
          <div style={{fontSize:9,color:T.textDim,marginBottom:5}}>BALL TARGET (m)</div>
          <div style={{display:"flex",gap:8,marginBottom:6}}>
            <NumField label="X" value={scenario.ballTarget?.x ?? ""}
              onChange={v=>onSetBallTarget({...scenario.ballTarget, x:v, y:scenario.ballTarget?.y ?? 34})}/>
            <NumField label="Y" value={scenario.ballTarget?.y ?? ""}
              onChange={v=>onSetBallTarget({...scenario.ballTarget, y:v, x:scenario.ballTarget?.x ?? 52.5})}/>
          </div>
          <div style={{fontSize:9,color:T.textDim,lineHeight:1.5}}>
            Pitch is 105×68m. Outcome estimates below are a deterministic
            heuristic (distance-to-goal, nearest opponent) — not a trained
            xG/xT model. Swap in a real model in scenarioEngine.js later.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{textAlign:"right"}}>
      <div style={{color:T.textDim,fontSize:8.5}}>{label}</div>
      <div style={{color:highlight?T.good:T.textPri,fontWeight:700,fontSize:11.5}}>{value}</div>
    </div>
  );
}

function NumField({ label, value, onChange }) {
  return (
    <label style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:T.textSec}}>
      {label}
      <input type="number" value={value}
        onChange={e=>onChange(e.target.value===""?null:parseFloat(e.target.value))}
        style={{width:56,background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,
          color:T.textPri,fontSize:10.5,padding:"3px 6px",fontFamily:"inherit"}}/>
    </label>
  );
}
